import * as THREE from "three";
import type { VFX } from "../../../../../../art/vfx.js";
import { SkinKits } from "./runtime";
import { KitClock } from "./clock";
import { FORTRESS_KITS } from "../kits";
import { hqNode } from "../../views/common";
import { KIT_BUDGET, type Flourish, type FlourishImpact, type FlourishMuzzle, type FortressKit, type IdleFx, type IdleKind } from "./types";

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _c = new THREE.Vector3(), _d = new THREE.Vector3();
/** The VFX module's raw particle spawn (art/vfx.js `spawn`, not in its .d.ts): the kit's own particle shapes. */
type Spawner = { spawn(p: Record<string, unknown>): unknown };
const rnd = (a: number, b: number): number => a + Math.random() * (b - a);
/** The paid skins' particle shapes (falling leaves, inward sparks, a collapsing ring): seconds and metres. */
const SHAPE = { leafLife: 2.4, leafFall: 0.22, leafSize: 0.085, inwardLife: 0.7, inwardShell: 0.42, implodeLife: 0.32, implodeSize: 0.9 } as const;
/** The big kits' flourish shapes: the wind-up's lead and shell, the shatter's throw, the pillar's rise (s, m). */
const BIG = { chargeLead: 0.35, chargeShell: 0.22, shatterSpeed: 2.2, shatterGravity: 9, pillarRise: 1.4, pillarRadius: 0.07 } as const;
const rgb = (hex: number): [number, number, number] => { const c = new THREE.Color(hex); return [c.r, c.g, c.b]; };

/**
 * The kit's sprite effects in a level (the hub and the previews have no VFX module: there the attachments' own
 * twinkles carry the sparkle). Gated: nothing under Reduced effects, and nothing at the lowest tiers
 * (KitClock.gates().particles); particle counts are clamped to the kit budget whatever a kit asks.
 */
export class SkinFx {
  /** A tower fires its flourish at most this often (a fast archer would otherwise sparkle constantly). */
  static readonly FlourishGapSeconds = 0.3;
  /** The `blaze` flourish's heat-haze glow: a dim warm white, so it reads as air shimmering rather than more fire. */
  static readonly HeatHaze = 0x6b5a4a;
  private static readonly idleIn = new WeakMap<THREE.Object3D, number>();
  private static readonly lastFlourish = new WeakMap<THREE.Object3D, number>();
  /** Per tower: which of its kit's impact variants the next shot plays (round-robin). */
  private static readonly variantAt = new WeakMap<THREE.Object3D, number>();
  /** The fortress model whose strike colours the VFX module wears (set from the fortress view's frame). */
  private static readonly strikeWorn = new WeakMap<VFX, string>();

  /** Idle burst for a dressed tower model (call every frame from the tower views; `root` = the model's storeys). */
  static idle(root: THREE.Object3D, dt: number, fx: VFX): void {
    const kit = SkinFx.kitUnder(root);
    if (!kit?.idle || !KitClock.gates().particles) return;
    const left = (SkinFx.idleIn.get(root) ?? Math.random() * kit.idle.every[1]) - dt;
    if (left > 0) { SkinFx.idleIn.set(root, left); return; }
    const [lo, hi] = kit.idle.every;
    SkinFx.idleIn.set(root, lo + Math.random() * (hi - lo));
    const tower = SkinKits.dressedTower(root);
    const fit = tower ? kit.idle.fit?.[tower] : undefined;
    const at = SkinFx.socketUnder(root, fit?.at ?? kit.idle.at, _a);
    if (at) SkinFx.burst(fx, kit.idle, at.setY(at.y + (fit?.lift ?? kit.idle.lift ?? 0)));
  }

  /**
   * The skin's flourish for a shot: a glint at the muzzle on the clip's release (`release` s from now, where `muzzle`
   * says the muzzle is then), and the impact at the target `to` when the round lands (`land` s from now).
   */
  static flourish(root: THREE.Object3D, fx: VFX, muzzle: (() => THREE.Vector3 | null) | null, to: THREE.Vector3, release: number, land: number): void {
    const kit = SkinFx.kitUnder(root);
    if (!kit?.flourish || !KitClock.gates().particles) return;
    const now = performance.now() / 1000;
    const f = kit.flourish;
    const inGap = now - (SkinFx.lastFlourish.get(root) ?? -Infinity) < SkinFx.FlourishGapSeconds;
    if (inGap && !f.everyShot) return;
    if (!inGap) SkinFx.lastFlourish.set(root, now);
    const k = inGap ? 0.5 : 1;                      // an every-shot kit's shots inside the gap: a half-size burst
    const tower = SkinKits.dressedTower(root);
    const per = tower ? f.towers?.[tower] : undefined;
    const m = per?.muzzle ?? f.muzzle, charge = per?.charge ?? f.charge;
    const n = (count: number | undefined, dflt: number) => Math.max(1, Math.round(Math.min(KIT_BUDGET.FlourishParticles, count ?? dflt) * k));
    if (charge && muzzle) {
      // the wind-up: sparks drawn in onto the muzzle, `lead` s before the round leaves
      const lead = charge.lead ?? BIG.chargeLead, c = n(charge.count, 4);
      const pull = () => { const at = muzzle(); if (at) SkinFx.inward(fx, _b.copy(at), c, rgb(charge.colour), BIG.chargeShell); };
      if (release > lead) fx.later(release - lead, pull); else pull();
    }
    if (m && muzzle) {
      const c = n(m.count, 3);
      const glint = () => { const at = muzzle(); if (at) SkinFx.muzzle(fx, m, _b.copy(at), c); };
      if (release > 0) fx.later(release, glint); else glint();
    }
    if (f.strike) {
      // the blow on the tower's own anvil, landing with the release (the charge-driven hammer slams down then)
      const s = f.strike, off = (tower ? s.fit?.[tower] : undefined) ?? s.offset;
      const blow = () => { const at = SkinFx.socketUnder(root, s.at, _c, off); if (at) SkinFx.impact(fx, { kind: "sparks", colour: s.colour, count: n(s.count, 5) }, at); };
      if (release > 0) fx.later(release, blow); else blow();
    }
    const own = per && (per.impact || per.impacts) ? per : f;      // a tower's own impact(s) replace the kit's
    const imp = SkinFx.impactFor(root, own.impacts, own.impact);
    if (imp) {
      const at = to.clone();
      const scaled = inGap ? { ...imp, count: n(imp.count, 5) } : imp;
      const hit = () => SkinFx.impact(fx, scaled, at);
      if (land > 0) fx.later(land, hit); else hit();
    }
  }

  /** The impact a tower's next shot plays: its kit's variants in turn (round-robin per tower), else its one impact. */
  static impactFor(root: THREE.Object3D, impacts: readonly FlourishImpact[] | undefined, impact: FlourishImpact | undefined): FlourishImpact | undefined {
    if (!impacts?.length) return impact;
    const i = SkinFx.variantAt.get(root) ?? 0;
    SkinFx.variantAt.set(root, (i + 1) % impacts.length);
    return impacts[i % impacts.length];
  }

  /** The muzzle flourish of its kind: stars (default), a hot spray, an upright shock ring or embers. */
  private static muzzle(fx: VFX, m: FlourishMuzzle, at: THREE.Vector3, n: number): void {
    const color = rgb(m.colour);
    if (m.kind === "sparks") fx.sparks(at, n, { color, speed: 1.2, up: 0.6, life: 0.3, size: 0.05 });
    else if (m.kind === "ring") fx.ring(at, { size: 0.32, life: 0.25, color, flat: false, bright: 1.6 });
    else if (m.kind === "embers") fx.embers(at, n, { color, radius: 0.06, life: 0.6, size: 0.06 });
    else fx.sparkles(at, n, { color, radius: 0.08, life: 0.45, size: 0.09 });
  }

  /** The fortress kit of a fortress model name (`base_t2__moonstone` → the Moonstone Keep), or null for stock. */
  static fortressKit(modelName: string): FortressKit | null {
    const model = modelName.split("__")[1];
    return model ? Object.values(FORTRESS_KITS).find((k) => k.model === model) ?? null : null;
  }

  /**
   * The fortress skin's idle burst (call every frame for the fortress view): from its `<model>.<node>` part, every few
   * seconds. Same gates and budget as a tower's idle. Also keeps the VFX module's strike colours on this fortress's.
   */
  static fortressIdle(root: THREE.Object3D, modelName: string, dt: number, fx: VFX): void {
    if (SkinFx.strikeWorn.get(fx) !== modelName) { SkinFx.strikeWorn.set(fx, modelName); SkinFx.wearStrike(fx, modelName); }
    const idle = SkinFx.fortressKit(modelName)?.idle;
    if (!idle || !KitClock.gates().particles) return;
    const left = (SkinFx.idleIn.get(root) ?? Math.random() * idle.every[1]) - dt;
    if (left > 0) { SkinFx.idleIn.set(root, left); return; }
    SkinFx.idleIn.set(root, idle.every[0] + Math.random() * (idle.every[1] - idle.every[0]));
    const node = hqNode(root, modelName, idle.node);
    if (node) SkinFx.burst(fx, { ...idle, at: "apex" }, node.getWorldPosition(_a));
  }

  /** Wears the fortress skin's strike colours on the VFX module (null: the stock arcane violet). */
  static wearStrike(fx: VFX, modelName: string | null): void {
    const s = modelName ? SkinFx.fortressKit(modelName)?.strike : undefined;
    fx.strikePalette = s ? { core: [...s.core], rim: [...s.rim], hex: s.hex, coreHex: s.coreHex } : null;
  }

  static impact(fx: VFX, imp: NonNullable<Flourish["impact"]>, at: THREE.Vector3): void {
    const n = Math.min(KIT_BUDGET.FlourishParticles, imp.count ?? 5), color = rgb(imp.colour);
    if (imp.kind === "sparks") { fx.sparks(at, n, { color, speed: 1.6, up: 1.2, life: 0.4, size: 0.06 }); fx.flash(at, 0.22, color); }
    else if (imp.kind === "ring") fx.ring(at, { size: 0.5, life: 0.35, color, flat: true, bright: 1.6 });
    else if (imp.kind === "splash") {
      // a flat ring on the ground and droplets thrown up that fall back (sparks with low lift and full gravity)
      fx.ring(at, { size: 0.55, life: 0.4, color, flat: true, bright: 1.4 });
      fx.sparks(_b.copy(at).setY(at.y + 0.04), n, { color, speed: 0.9, up: 2.2, gravity: 6, life: 0.5, size: 0.05 });
    } else if (imp.kind === "blaze") {
      // embers up, a hot core flash, and a wide dim glow that swells and fades behind it: the heat shimmer
      fx.embers(at, n, { color, radius: 0.12, life: 0.7, size: 0.08 });
      fx.flash(at, 0.3, color);
      fx.glowPulse(_b.copy(at).setY(at.y + 0.12), { size: 0.7, life: 0.45, color: rgb(SkinFx.HeatHaze) });
    }
    else if (imp.kind === "radiant") { fx.flash(at, 0.5, [1, 1, 0.95]); fx.ring(at, { size: 0.7, life: 0.4, color, flat: false, bright: 1.8 }); fx.sparkles(at, n, { color, radius: 0.18, rise: 0.6, life: 0.6, size: 0.1 }); }
    else if (imp.kind === "warfire") { fx.flash(at, 0.42, color); fx.sparks(at, n, { color, speed: 1.9, up: 1.5, life: 0.45, size: 0.07 }); fx.embers(at, Math.ceil(n / 2), { color, radius: 0.12, life: 0.8, size: 0.06 }); }
    else if (imp.kind === "leafburst") { SkinFx.leaves(fx, at, n, color, true); fx.sparkles(at, Math.ceil(n / 2), { color: [1, 0.72, 0.28], radius: 0.1, rise: 0.3, life: 0.5, size: 0.08 }); }
    else if (imp.kind === "implode") SkinFx.implode(fx, at, n, color);
    else if (imp.kind === "shatter") {
      // shards thrown out low and falling hard, a cold flash
      fx.sparks(at, n, { color, speed: BIG.shatterSpeed, up: 1.0, gravity: BIG.shatterGravity, life: 0.45, size: 0.06 });
      fx.flash(at, 0.3, [0.9, 0.95, 1]);
    } else if (imp.kind === "pillar") {
      // a column of sparkles and embers rising from the hit, a flash at its foot
      fx.flash(at, 0.3, color);
      fx.sparkles(at, n, { color, radius: BIG.pillarRadius, rise: BIG.pillarRise, life: 0.7, size: 0.09 });
      fx.embers(at, Math.ceil(n / 2), { color, radius: BIG.pillarRadius, life: 0.8, size: 0.06 });
    }
    else { fx.sparkles(at, n, { color, radius: 0.16, rise: 0.25, life: 0.55, size: 0.11 }); fx.flash(at, 0.35, color); }
  }

  private static burst(fx: VFX, idle: IdleFx, at: THREE.Vector3): void {
    const n = Math.min(KIT_BUDGET.IdleParticles, idle.count);
    SkinFx.spray(fx, idle.kind, n, rgb(idle.colour), idle.radius ?? 0.2, at);
    const also = idle.also;
    if (also) SkinFx.spray(fx, also.kind, Math.min(KIT_BUDGET.IdleParticles - n, also.count), rgb(also.colour), also.radius ?? 0.2, at);
  }

  private static spray(fx: VFX, kind: IdleKind, n: number, color: [number, number, number], radius: number, at: THREE.Vector3): void {
    if (n <= 0) return;
    if (kind === "embers") fx.embers(at, n, { color, radius, life: 1.2, size: 0.07 });
    else if (kind === "motes") fx.sparkles(at, n, { color, radius, rise: 0.5, life: 1.6, size: 0.06 });
    else if (kind === "steam") fx.smoke(at, n, { size: 0.16, life: 1.1, rise: 0.45, spread: 0.08, color, alpha: 0.55 });
    else if (kind === "drips") fx.sparks(_b.copy(at).add(_c.set((Math.random() - 0.5) * radius, 0, (Math.random() - 0.5) * radius)), n, { color, speed: 0.12, up: 0.05, gravity: 5, life: 0.55, size: 0.045 });
    else if (kind === "leaves") SkinFx.leaves(fx, at, n, color, false, radius);
    else if (kind === "inward") SkinFx.inward(fx, at, n, color, SHAPE.inwardShell);
    else fx.sparkles(at, n, { color, radius, rise: kind === "glints" ? 0.05 : 0.3, life: 0.9, size: 0.09 });
  }

  /** Leaf sprites: drifting down and turning from a crown (`thrown` false), or flung out of an impact (true). */
  private static leaves(fx: VFX, at: THREE.Vector3, n: number, color: [number, number, number], thrown: boolean, radius = 0.2): void {
    const sp = fx as unknown as Spawner;
    for (let i = 0; i < n; i++) {
      const pos = _c.copy(at).add(_d.set(rnd(-radius, radius), rnd(-0.05, 0.05), rnd(-radius, radius)));
      const vel = thrown ? _b.set(rnd(-1, 1), rnd(0.8, 1.5), rnd(-1, 1)).multiplyScalar(1.3) : _b.set(rnd(-0.12, 0.12), -SHAPE.leafFall, rnd(-0.12, 0.12));
      const shade = rnd(0.75, 1.15);
      sp.spawn({ pos, vel, frame: "leaf", gravity: thrown ? 3 : 0.05, drag: thrown ? 1.4 : 0.3, life: thrown ? rnd(0.6, 1) : SHAPE.leafLife * rnd(0.8, 1.2), size0: SHAPE.leafSize, color0: color.map((c) => c * shade), additive: false, rot: rnd(0, 6.3), rotVel: rnd(-3, 3), fadeIn: 0.15, fadeOut: 0.3, floor: thrown ? at.y - 0.2 : undefined });
    }
  }

  /** Sparks drawn in from a shell of radius `shell` onto `at` (the void's pull). */
  private static inward(fx: VFX, at: THREE.Vector3, n: number, color: [number, number, number], shell: number): void {
    const sp = fx as unknown as Spawner;
    for (let i = 0; i < n; i++) {
      _d.set(rnd(-1, 1), rnd(-0.6, 0.8), rnd(-1, 1)).normalize().multiplyScalar(shell);
      const life = SHAPE.inwardLife * rnd(0.8, 1.2);
      sp.spawn({ pos: _c.copy(at).add(_d), vel: _b.copy(_d).multiplyScalar(-1 / life), frame: "spark", life, size0: 0.09, size1: 0.02, aspect: 0.35, stretch: 0.3, color0: [1, 1, 1], color1: color, bright: 2.2, fadeIn: 0.2, fadeOut: 0.2 });
    }
  }

  /** A ring that collapses onto `at`, sparks pulled in, and a pop when it closes. */
  private static implode(fx: VFX, at: THREE.Vector3, n: number, color: [number, number, number]): void {
    const sp = fx as unknown as Spawner;
    _a.copy(at); _a.y += 0.12;
    sp.spawn({ pos: _a.clone(), anim: "shock", life: SHAPE.implodeLife, size0: SHAPE.implodeSize, size1: 0.08, fadeIn: 0.05, fadeOut: 0.2, color0: color, bright: 2.2 });
    SkinFx.inward(fx, _a, n, color, SHAPE.implodeSize * 0.45);
    const pop = _a.clone();
    fx.later(SHAPE.implodeLife, () => { fx.flash(pop, 0.35, color); });
  }

  /** The kit worn by the tower model under `root` (the views keep the storeys group; SkinKits.hostOf finds the clone). */
  private static kitUnder(root: THREE.Object3D) { return SkinKits.dressedKit(root); }

  private static socketUnder(root: THREE.Object3D, name: Parameters<typeof SkinKits.socketWorld>[1], out: THREE.Vector3, offset?: readonly [number, number, number]): THREE.Vector3 | null {
    return SkinKits.socketWorld(root, name, out, offset);
  }
}
